import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import BasePageLayout from '../../components/BasePageLayout';
import { useAuth } from '../../context/AuthContext';
import { useLanguage } from '../../context/LanguageContext';
import { forumService } from '../../services/forumService';
import type { ForumCategory } from '../../types/forum';
import '../../styles/pages/CreateTopic.css';
import '../../styles/pages/TasksHubModern.css';
import '../../styles/pages/ForumCategory.css';

const CreateTopicPage: React.FC = () => {
  const { categorySlug } = useParams<{ categorySlug: string }>();
  const { user } = useAuth();
  const { t } = useLanguage();
  const navigate = useNavigate();

  // Form state
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [tags, setTags] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [category, setCategory] = useState<ForumCategory | null>(null);

  // Current tag input
  const [currentTag, setCurrentTag] = useState('');

  // Poll state
  const [isPollEnabled, setIsPollEnabled] = useState(false);
  const [pollQuestion, setPollQuestion] = useState('');
  const [pollOptions, setPollOptions] = useState<string[]>(['', '']);

  useEffect(() => {
    const loadCategory = async () => {
      if (categorySlug) {
        try {
          const categoryData = await forumService.getCategoryBySlugDirect(categorySlug);
          if (categoryData) {
            setCategory(categoryData);
          }
        } catch (err) {
          console.error('Error loading category:', err);
        }
      }
    };
    loadCategory();
  }, [categorySlug]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!user) {
      setError(t('forums.createTopic.errors.loginRequired'));
      return;
    }

    if (!title.trim() || !description.trim()) {
      setError(t('forums.createTopic.errors.requiredFields'));
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const topicData: any = {
        title: title.trim(),
        description: description.trim(),
        tags,
      };

      if (isPollEnabled) {
        if (!pollQuestion.trim()) {
          setError(t('forums.createTopic.errors.pollQuestionRequired'));
          setIsLoading(false);
          return;
        }

        const validOptions = pollOptions.filter(o => o.trim());
        if (validOptions.length < 2) {
          setError(t('forums.createTopic.errors.pollOptionsRequired'));
          setIsLoading(false);
          return;
        }

        topicData.poll = {
          question: pollQuestion.trim(),
          options: validOptions
        };
      }

      await forumService.createTopicByCategorySlug(categorySlug!, topicData);

      // Navigate back to the category topics list
      navigate(`/forums/category/${categorySlug}`);
    } catch (err) {
      console.error('Error creating topic:', err);
      setError(err instanceof Error ? err.message : t('forums.createTopic.errors.failed'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleAddTag = () => {
    const tag = currentTag.trim().toLowerCase();
    if (tag && !tags.includes(tag) && tags.length < 5) {
      setTags([...tags, tag]);
      setCurrentTag('');
    }
  };

  const handleRemoveTag = (index: number) => {
    setTags(tags.filter((_, i) => i !== index));
  };

  const handleTagKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAddTag();
    }
  };

  return (
    <BasePageLayout maxWidth="none" background="white">
      <div className="create-topic-page">
        {/* Header matching ForumCategoryPage structure */}
        <div className="tasks-hub-header">
          <div className="tasks-hub-header-content">
            <div className="category-header-modern">
              <div className="category-breadcrumb" style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap' }}>
                <Link to="/forums" className="breadcrumb-link">
                  <i className="bi bi-house-door"></i>
                  {t('forums.topic.breadcrumbs.forums')}
                </Link>
                <span className="breadcrumb-separator mx-2">/</span>
                <Link
                  to={`/forums/category/${categorySlug}`}
                  className="breadcrumb-link"
                >
                  {category ? t(`forums.categories.titles.${category.slug}`, { defaultValue: category.title }) : t('forums.common.backToCategory')}
                </Link>
              </div>

              <div className="category-header-content">
                {category && (
                  <div className="category-icon-wrapper" style={{ backgroundColor: `${category.color}15`, color: category.color }}>
                    <span className="category-icon">{category.icon}</span>
                  </div>
                )}
                <div className="category-info">
                  <h1>{t('forums.createTopic.title')}</h1>
                  <p className="description-text">{t('forums.createTopic.subtitle')}</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Error Message */}
        {error && (
          <div className="error-message">
            <i className="bi bi-exclamation-circle"></i>
            {error}
          </div>
        )}

        {/* Create Topic Form */}
        <form onSubmit={handleSubmit} className="create-topic-form">
          {/* Title */}
          <div className="form-group">
            <label htmlFor="title" className="form-label">
              {t('forums.createTopic.form.titleLabel')} <span className="required">*</span>
            </label>
            <input
              type="text"
              id="title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder={t('forums.createTopic.form.titlePlaceholder')}
              className="form-input"
              maxLength={200}
              required
            />
            <div className="char-count">{title.length}/200</div>
          </div>

          {/* Description */}
          <div className="form-group">
            <label htmlFor="description" className="form-label">
              {t('forums.createTopic.form.descriptionLabel')} <span className="required">*</span>
            </label>
            <textarea
              id="description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder={t('forums.createTopic.form.descriptionPlaceholder')}
              className="form-textarea"
              rows={8}
              required
            />
            <div className="char-count">{description.length} characters</div>
            <div className="form-help">
              {t('forums.createTopic.form.descriptionHelp')}
            </div>
          </div>

          {/* Tags */}
          <div className="form-group">
            <label className="form-label">{t('forums.createTopic.form.tagsLabel')}</label>
            <div className="tags-input-container">
              <div className="tags-display">
                {tags.map((tag, index) => (
                  <span key={index} className="tag">
                    {tag}
                    <button
                      type="button"
                      onClick={() => handleRemoveTag(index)}
                      className="tag-remove"
                      aria-label={`Remove tag ${tag}`}
                    >
                      ×
                    </button>
                  </span>
                ))}
              </div>
              <div className="tag-input-row">
                <input
                  type="text"
                  value={currentTag}
                  onChange={(e) => setCurrentTag(e.target.value)}
                  onKeyPress={handleTagKeyPress}
                  placeholder={t('forums.createTopic.form.tagsPlaceholder')}
                  className="tag-input"
                  maxLength={20}
                  disabled={tags.length >= 5}
                />
                <button
                  type="button"
                  onClick={handleAddTag}
                  className="tag-add-btn"
                  disabled={!currentTag.trim() || tags.length >= 5}
                >
                  Add
                </button>
              </div>
              <div className="form-help">
                {t('forums.createTopic.form.tagsHelp')}
              </div>
            </div>
          </div>

          {/* Poll Creation */}
          <div className="form-group">
            <div className="poll-toggle-header">
              <label className="toggle-switch">
                <input
                  type="checkbox"
                  checked={isPollEnabled}
                  onChange={(e) => setIsPollEnabled(e.target.checked)}
                />
                <span className="toggle-slider"></span>
              </label>
              <span className="toggle-label">{t('forums.createTopic.form.pollToggle')}</span>
            </div>

            {isPollEnabled && (
              <div className="poll-creation-section">
                <div className="form-group">
                  <label htmlFor="poll-question" className="form-label">
                    {t('forums.createTopic.form.pollQuestion')} <span className="required">*</span>
                  </label>
                  <input
                    type="text"
                    id="poll-question"
                    value={pollQuestion}
                    onChange={(e) => setPollQuestion(e.target.value)}
                    placeholder={t('forums.createTopic.form.pollQuestionPlaceholder')}
                    className="form-input"
                    maxLength={200}
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">
                    {t('forums.createTopic.form.pollOptions')} <span className="required">*</span>
                  </label>
                  <div className="poll-options-list">
                    {pollOptions.map((option, index) => (
                      <div key={index} className="poll-option-row">
                        <input
                          type="text"
                          value={option}
                          onChange={(e) => {
                            const newOptions = [...pollOptions];
                            newOptions[index] = e.target.value;
                            setPollOptions(newOptions);
                          }}
                          placeholder={`Option ${index + 1}`}
                          className="form-input"
                          maxLength={100}
                        />
                        {pollOptions.length > 2 && (
                          <button
                            type="button"
                            onClick={() => {
                              const newOptions = pollOptions.filter((_, i) => i !== index);
                              setPollOptions(newOptions);
                            }}
                            className="btn-icon-danger"
                            title="Remove option"
                          >
                            <i className="bi bi-trash"></i>
                          </button>
                        )}
                      </div>
                    ))}
                  </div>

                  {pollOptions.length < 10 && (
                    <button
                      type="button"
                      onClick={() => setPollOptions([...pollOptions, ''])}
                      className="btn-text-primary"
                    >
                      <i className="bi bi-plus"></i> {t('forums.createTopic.form.addOption')}
                    </button>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Submit Actions */}
          <div className="form-actions">
            <button
              type="button"
              onClick={() => navigate(`/forums/category/${categorySlug}`)}
              className="btn-secondary"
              disabled={isLoading}
            >
              {t('forums.createTopic.form.cancel')}
            </button>
            <button
              type="submit"
              className="btn-primary"
              disabled={isLoading || !title.trim() || !description.trim()}
            >
              {isLoading ? (
                <>
                  <i className="bi bi-arrow-clockwise spin"></i>
                  {t('forums.createTopic.form.submitting')}
                </>
              ) : (
                <>
                  <i className="bi bi-plus-circle"></i>
                  {t('forums.createTopic.form.submit')}
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </BasePageLayout>
  );
};

export default CreateTopicPage;
